import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export function GoldButton({ children, to, href, onClick, testId, size = "md", type, className = "" }) {
  const cls = `group relative inline-flex items-center gap-2 rounded-full bg-bht-gold px-8 py-4 font-sans text-[13px] font-semibold uppercase tracking-[0.14em] text-bht-void transition-[transform,box-shadow] duration-500 ease-out hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(200,160,69,0.45)] ${size === "sm" ? "px-5 py-3 text-[11px]" : ""} ${className}`;
  const inner = (
    <>
      <span>{children}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="transition-transform duration-500 group-hover:translate-x-1" aria-hidden>
        <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </>
  );
  if (to) return <Link to={to} data-testid={testId} className={cls}>{inner}</Link>;
  if (href) return <a href={href} data-testid={testId} className={cls}>{inner}</a>;
  return <button type={type || "button"} onClick={onClick} data-testid={testId} className={cls}>{inner}</button>;
}

export function GhostButton({ children, to, onClick, testId, className = "" }) {
  const cls = `inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.02] px-6 py-3 text-[13px] font-medium text-white/90 transition-colors duration-300 hover:border-bht-gold/60 hover:text-bht-gold ${className}`;
  if (to) return <Link to={to} data-testid={testId} className={cls}>{children}</Link>;
  return <button type="button" onClick={onClick} data-testid={testId} className={cls}>{children}</button>;
}

export function Eyebrow({ children, className = "" }) {
  return (
    <span className={`eyebrow inline-flex items-center gap-2 ${className}`}>
      <span className="inline-block h-px w-6 bg-bht-gold/80" />
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, sub, align = "left", className = "" }) {
  return (
    <div className={`${align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl"} ${className}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="mt-5 font-serif text-4xl leading-[1.1] tracking-tight sm:text-5xl md:text-6xl"
      >
        {title}
      </motion.h2>
      {sub && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg"
        >
          {sub}
        </motion.p>
      )}
    </div>
  );
}

export function GlassCard({ children, className = "", testId, hover = true }) {
  return (
    <motion.div
      whileHover={hover ? { y: -4 } : undefined}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      data-testid={testId}
      className={`glass rounded-2xl p-8 ${className}`}
    >
      {children}
    </motion.div>
  );
}

export function AmbientOrbs({ variant = "default" }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {variant === "gold" ? (
        <>
          <div className="absolute -left-32 top-10 h-[380px] w-[380px] rounded-full radial-gold orb-anim" />
          <div className="absolute right-[-80px] bottom-20 h-[320px] w-[320px] rounded-full radial-cyan" />
        </>
      ) : (
        <>
          <div className="absolute -left-24 top-24 h-[420px] w-[420px] rounded-full radial-purple orb-anim" />
          <div className="absolute right-[-120px] bottom-[-60px] h-[460px] w-[460px] rounded-full radial-cyan orb-anim" />
        </>
      )}
    </div>
  );
}

export function StatChip({ value, label, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col gap-1 border-l border-white/10 pl-6">
      <div className="font-serif text-3xl leading-none tracking-tight sm:text-4xl">{value}</div>
      <div className="eyebrow">{label}</div>
    </div>
  );
}
